import createFetchClient, {} from 'openapi-fetch';
import { ApiError, AuthRequiredError, NetworkError, TotpStepUpRequiredError, } from './errors.js';
/**
 * Create a configured API client.
 *
 * Three things this wrapper does beyond raw openapi-fetch:
 *
 * 1. Forces `credentials: 'include'` on every request so the httpOnly
 *    session cookie set by /api/v1/auth/* travels cross-origin (dev:
 *    portal :5173 → backend :3000). Without this, every request would
 *    be unauthenticated.
 *
 * 2. Detects 401 responses globally, invokes the registered handler
 *    (typically `navigate('/sign-in')`), and throws AuthRequiredError
 *    so the caller's `await` chain stops cleanly. The {data, error}
 *    pattern from openapi-fetch is preserved for non-401 errors.
 *
 * 3. Normalises network-level failures (fetch throws) into NetworkError
 *    so consumers can pattern-match on instanceof ApiError uniformly.
 */
export function createApiClient(config) {
    const fetchImpl = config.fetch ?? globalThis.fetch.bind(globalThis);
    // openapi-fetch lets us pass middleware to inspect response. We use
    // this to detect 401 and invoke the on401 callback.
    //
    // Phase 14b-1-4: do NOT fire on401 for the step-up-required variant of
    // 401 — the session is still valid, the server just wants a fresh
    // TOTP. The admin app's MutationCache handler opens a step-up modal
    // instead of redirecting to sign-in. Detection here is best-effort:
    // we peek at the body via clone() so the original response is left
    // intact for openapi-fetch to consume.
    const authMiddleware = {
        onResponse: async ({ response }) => {
            if (response.status === 401) {
                let isStepUp = false;
                try {
                    const cloned = response.clone();
                    const body = await cloned.json();
                    if (body !== null &&
                        typeof body === 'object' &&
                        'reason' in body &&
                        body.reason === 'totp_step_up_required') {
                        isStepUp = true;
                    }
                }
                catch {
                    // Body wasn't JSON (or was already consumed somehow). Treat
                    // as a normal 401 — fall through to on401.
                }
                if (!isStepUp) {
                    try {
                        config.on401?.();
                    }
                    catch (err) {
                        // The on401 callback should never throw — but if it does
                        // (e.g. router error), surface it via console.error rather
                        // than swallowing silently.
                        console.error('on401 handler threw:', err);
                    }
                }
            }
            return undefined;
        },
    };
    // Wrap the underlying fetch to surface network errors as typed
    // NetworkError instances. openapi-fetch passes through fetch errors
    // verbatim; we want a uniform error surface.
    const wrappedFetch = async (input, init) => {
        try {
            return await fetchImpl(input, init);
        }
        catch (err) {
            throw new NetworkError(err instanceof Error ? err.message : 'Network request failed', err);
        }
    };
    const client = createFetchClient({
        baseUrl: config.baseUrl,
        fetch: wrappedFetch,
        credentials: 'include',
    });
    client.use(authMiddleware);
    return client;
}
/** Helper for callers that prefer throwing over the {data, error} pattern.
 *  Usage:
 *    const me = await unwrap(api.GET('/api/v1/auth/me'));
 *
 *  Throws AuthRequiredError on 401, ApiError on other non-2xx, or
 *  returns the data on success. */
export async function unwrap(result) {
    const { data, error, response } = await result;
    if (error !== undefined) {
        if (response.status === 401) {
            // Phase 14b-1-4 — discriminate the step-up-required variant of
            // 401 so the admin app's MutationCache handler can pattern-match
            // `instanceof TotpStepUpRequiredError` to open the step-up modal
            // (and the redirect-to-sign-in path stays untouched).
            if (error !== null &&
                typeof error === 'object' &&
                'reason' in error &&
                error.reason === 'totp_step_up_required') {
                throw new TotpStepUpRequiredError(error);
            }
            throw new AuthRequiredError(error);
        }
        throw new ApiError(`API ${response.status} ${response.statusText}`, response.status, error);
    }
    if (data === undefined) {
        // A body-less 2xx (204 No Content, or any success with no JSON body) is a
        // legitimate success — openapi-fetch returns { data: undefined, error:
        // undefined } for it. Treating that as an error made every 204 endpoint
        // (password change, availability delete, …) surface a false failure to the
        // user even though the write succeeded. Only a NON-ok response with neither
        // data nor error is genuinely malformed.
        if (response.ok) {
            return undefined;
        }
        throw new ApiError('No data and no error in API response', response.status, null);
    }
    return data;
}
//# sourceMappingURL=client.js.map